import * as Sentry from "@sentry/react";
import type {
  AnyRoute,
  Router,
  TrailingSlashOption,
} from "@tanstack/react-router";

import { getEnv } from "../environment";

import ErrorBoundary from "./bug/error-boundary";

const { tag, version, environment } = getEnv();

// This is the only place where we should be importing @sentry/react
// if functionality you need is missing, consider adding it here as a wrapped export

function init<
  T extends AnyRoute,
  S extends TrailingSlashOption,
  B extends boolean,
>(router?: Router<T, S, B>) {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    // eslint-disable-next-line no-console
    console.warn("VITE_SENTRY_DSN not set — Sentry disabled");
    return;
  }

  // eslint-disable-next-line no-console
  console.log("Initializing bug tracker");

  Sentry.init({
    release: `bfc-web-ui-${environment}@${tag ?? version}`,
    dsn,
    integrations: [
      router
        ? Sentry.tanstackRouterBrowserTracingIntegration(router)
        : Sentry.browserTracingIntegration(),
    ],
    enableLogs: true,
    sendDefaultPii: true,
    tracesSampleRate: 0.5,
    // eslint-disable-next-line no-useless-escape
    tracePropagationTargets: [/^https:\/\/[\w\.]*\.bfc\.software$/],
  });
}

function captureException(exception: unknown, extra?: Record<string, unknown>) {
  Sentry.captureException(exception, extra);
}

type SentryLogger = typeof Sentry.logger;
type ConsoleLevel = "debug" | "info" | "warn" | "error";

// All Sentry.logger methods share the same signature
type LogMethod = SentryLogger["info"];

const consoleLevelMap: Record<string, ConsoleLevel> = {
  trace: "debug",
  debug: "debug",
  info: "info",
  warn: "warn",
  error: "error",
  fatal: "error",
};

function withConsole(level: string, sentryMethod: LogMethod): LogMethod {
  const stdConsoleFnName = consoleLevelMap[level];

  return (...args: Parameters<LogMethod>) => {
    // eslint-disable-next-line no-console
    console[stdConsoleFnName](...args);
    sentryMethod(...args);
  };
}

export const logger: SentryLogger = {
  trace: withConsole("trace", Sentry.logger.trace),
  debug: withConsole("debug", Sentry.logger.debug),
  info: withConsole("info", Sentry.logger.info),
  warn: withConsole("warn", Sentry.logger.warn),
  error: withConsole("error", Sentry.logger.error),
  fatal: withConsole("fatal", Sentry.logger.fatal),
  fmt: Sentry.logger.fmt,
};

export default {
  init,
  captureException,
  ErrorBoundary,
};
